"use client"

import { memo } from "react"
import dynamic from "next/dynamic"

import { GetRelatedRestoResponse, RestoDetailData } from "@/lib/types"

import { MenuSkeleton } from "./components/menu-skeleton"
// Import the lightweight components directly
import { RestaurantHeader } from "./components/restaurant-header"
import { RestaurantRatingSection } from "./components/restaurant-rating-section"

// Dynamically import heavy components with matching skeletons
// to prevent layout shifts while maintaining consistent loading UI
const MenuCategories = dynamic(
  () =>
    import("./components/menu-categories").then((mod) => mod.MenuCategories),
  {
    ssr: true,
    loading: () => <MenuSkeleton />,
  }
)

interface RestoDetailProps {
  detail: RestoDetailData
  hasGeo?: boolean
  relatedRestoPromise?: Promise<GetRelatedRestoResponse>
}

export const RestoDetail = memo(function RestoDetail({
  detail,
  hasGeo = false,
  relatedRestoPromise,
}: RestoDetailProps) {
  const {
    restaurant_info: restaurant,
    restaurant_tier: tier,
    categories,
    flash_sale_categories,
  } = detail

  return (
    <main itemScope itemType="https://schema.org/Restaurant" className="pb-16">
      <meta itemProp="name" content={restaurant.name} />
      <meta itemProp="address" content={restaurant.address} />

      <>
        <RestaurantHeader
          restaurant={detail}
          tier={tier}
          hasGeo={hasGeo}
          relatedRestoPromise={relatedRestoPromise}
        />
      </>

      <div className="container">
        <MenuCategories
          categories={categories}
          flashSaleCategories={flash_sale_categories}
          isClosed={restaurant.is_closed}
        />
        <RestaurantRatingSection restaurantId={restaurant.restaurant_id} />
      </div>
    </main>
  )
})
